/**
 * Endpoints de **Logística → Ventas Crédito** (`/api/logistica/ventas-credito`).
 *
 * El listado va paginado en el servidor con los filtros del panel anterior
 * (fechas, cliente y número). El detalle y la edición (conductor, vale y
 * kilometraje) se piden por venta. La pantalla es sólo para administradores, como
 * en el panel anterior.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  ClienteCredito,
  VentaCreditoDetalle,
  VentaCreditoEditable,
  VentasCreditoFiltros,
  VentasCreditoRespuesta,
} from './ventas-credito.types';

export const ventasCreditoKeys = {
  lista: (filtros: VentasCreditoFiltros) => ['logistica', 'ventas-credito', filtros] as const,
  detalle: (id: number) => ['logistica', 'ventas-credito', 'detalle', id] as const,
  clientes: (busqueda: string) => ['logistica', 'ventas-credito', 'clientes', busqueda] as const,
};

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: VentasCreditoFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.numero) parametros.set('numero', filtros.numero);
  if (filtros.clienteId) parametros.set('cliente_id', filtros.clienteId);
  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const ventasCreditoApi = {
  listar: (
    filtros: VentasCreditoFiltros = {},
    signal?: AbortSignal,
  ): Promise<VentasCreditoRespuesta> =>
    apiClient.get<VentasCreditoRespuesta>(`/api/logistica/ventas-credito${cadenaDe(filtros)}`, {
      signal,
    }),

  detalle: (id: number, signal?: AbortSignal): Promise<{ venta: VentaCreditoDetalle }> =>
    apiClient.get<{ venta: VentaCreditoDetalle }>(`/api/logistica/ventas-credito/${id}`, {
      signal,
    }),

  clientes: (buscar: string, signal?: AbortSignal): Promise<{ clientes: ClienteCredito[] }> =>
    apiClient.get<{ clientes: ClienteCredito[] }>(
      `/api/logistica/ventas-credito/clientes?buscar=${encodeURIComponent(buscar)}`,
      { signal },
    ),

  actualizar: (
    id: number,
    venta: VentaCreditoEditable,
  ): Promise<{ message: string }> =>
    apiClient.put<{ message: string }>(`/api/logistica/ventas-credito/${id}`, {
      body: {
        conductor_dni: venta.conductor_dni,
        conductor_nombre: venta.conductor_nombre,
        placa: venta.placa,
        vale: venta.vale,
        kilometraje: venta.kilometraje === '' ? null : Number(venta.kilometraje),
      },
    }),
};

/** Ventas a crédito del filtro pedido. */
export function useVentasCredito(filtros: VentasCreditoFiltros = {}) {
  return useQuery({
    queryKey: ventasCreditoKeys.lista(filtros),
    queryFn: ({ signal }) => ventasCreditoApi.listar(filtros, signal),
  });
}

/** Detalle de una venta a crédito. */
export function useVentaCredito(id: number | null) {
  return useQuery({
    queryKey: ventasCreditoKeys.detalle(id ?? 0),
    queryFn: ({ signal }) => ventasCreditoApi.detalle(id as number, signal),
    enabled: id !== null,
  });
}

/** Clientes para el filtro (búsqueda en el servidor, desde 2 caracteres). */
export function useClientesCredito(buscar: string) {
  return useQuery({
    queryKey: ventasCreditoKeys.clientes(buscar),
    queryFn: ({ signal }) => ventasCreditoApi.clientes(buscar, signal),
    enabled: buscar.trim().length >= 2,
    staleTime: 5 * 60 * 1000,
  });
}

/** Guarda los datos editables de una venta y refresca listado y detalle. */
export function useEditarVentaCredito() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, venta }: { id: number; venta: VentaCreditoEditable }) =>
      ventasCreditoApi.actualizar(id, venta),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'ventas-credito'] });
    },
  });
}
